/**
 * Currency list helpers. Options come only from GET /v1/companies/currencies/.
 */

/**
 * @param {string} [search]
 * @returns {readonly [string, string]}
 */
export function companyCurrenciesQueryKey(search = '') {
  return ['company-currencies', search];
}

/**
 * @param {unknown} payload
 * @returns {unknown[]}
 */
export function extractCurrencyRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.currencies)) return payload.currencies;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

/**
 * @param {unknown} lastPage
 * @returns {string|undefined}
 */
export function getCurrencyNextPageParam(lastPage) {
  if (!lastPage || typeof lastPage !== 'object' || Array.isArray(lastPage)) {
    return undefined;
  }
  if (!lastPage.next) return undefined;
  try {
    const url = new URL(lastPage.next);
    return url.searchParams.get('page') || undefined;
  } catch {
    const match = String(lastPage.next).match(/[?&]page=(\d+)/);
    return match ? match[1] : undefined;
  }
}

/**
 * @param {unknown} raw
 * @returns {import('../types').CompanyCurrency | null}
 */
export function normalizeCurrency(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const code = raw.trim().toUpperCase();
    return code ? { code, name: '', symbol: '' } : null;
  }
  if (typeof raw !== 'object') return null;

  const code = String(
    raw.code ?? raw.currency ?? raw.currency_code ?? raw.iso_code ?? '',
  )
    .trim()
    .toUpperCase();
  if (!code) return null;

  return {
    code,
    name: String(raw.name ?? raw.currency_name ?? '').trim(),
    symbol: String(raw.symbol ?? raw.currency_symbol ?? '').trim(),
  };
}

/**
 * @param {Pick<import('../types').CompanyCurrency, 'code' | 'name' | 'symbol'>} currency
 * @returns {string}
 */
export function formatCurrencyOptionLabel({ code, name, symbol }) {
  const displayName = name || code;
  const nameWithCode = displayName === code ? code : `${displayName} (${code})`;
  if (symbol && symbol !== code) {
    return `${symbol} ${nameWithCode}`;
  }
  return nameWithCode;
}

/**
 * Label for a saved code that is missing from the current API page/search.
 * Uses Intl for display only — not an options source.
 *
 * @param {string} code
 * @returns {string}
 */
export function getCurrencyFallbackLabel(code) {
  if (!code) return '';

  let name = '';
  let symbol = '';
  try {
    name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) || '';
  } catch {
    name = '';
  }
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
    }).formatToParts(0);
    const currencyPart = parts.find((part) => part.type === 'currency');
    symbol = currencyPart?.value || '';
    if (symbol === code) symbol = '';
  } catch {
    symbol = '';
  }

  return formatCurrencyOptionLabel({ code, name, symbol });
}

/**
 * @param {{ pages?: unknown[] }} [data]
 * @returns {import('../types').CompanyCurrency[]}
 */
export function flattenCurrencyPages(data) {
  const seen = new Set();
  const currencies = [];
  const rows = data?.pages?.flatMap((page) => extractCurrencyRows(page)) || [];
  rows.forEach((row) => {
    const currency = normalizeCurrency(row);
    if (!currency || seen.has(currency.code)) return;
    seen.add(currency.code);
    currencies.push(currency);
  });
  return currencies;
}

/**
 * @param {import('../types').CompanyCurrency} currency
 * @returns {{ value: string, label: string }}
 */
export function currencyToSelectOption(currency) {
  return {
    value: currency.code,
    label: formatCurrencyOptionLabel(currency),
  };
}
